import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { LoadingButton } from '@mui/lab';
import { CheckRounded, DirectionsCar, ExpandMore, Flight, Hotel, ShoppingCart } from '@mui/icons-material';
import { Box, Button, Card, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Typography, useTheme } from '@mui/material';

import { Currency, SALE_ORDER_STATUS } from '@bandido/shared/constants';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { routes } from '@frontend/router/routes';

import { useCart } from '../CartContext';

export const CartSummaryAndPay = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { state, convert, isSaving } = useCart();
  const { ability } = useAuth();
  const navigate = useNavigate();

  const services = state?.items || [];
  const hotelServices = services.filter(service => service.type === 'hotel');
  const hasItems = services.length > 0;
  const saveEnabled = hasItems && ability && ability.can('create', 'Booking');

  const totalHoteles = hotelServices.reduce((sum, service) => sum + service.price, 0);

  const [anchorMenu, setAnchorMenu] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorMenu);
  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorMenu(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorMenu(null);
  };
  const handleConvertToSaleOrder = async () => {
    const result = await convert();
    if(result) {
      navigate(`${routes.dashboard.saleOrder.detail}`.replace(':id', result.id));
    }
  };
  return (
    <Card>
      <Box
        width="354px"
        p={2}
        flexDirection="column"
        alignItems="flex-start"
        gap={5}
      >
        <Box
          display="flex"
          pt={2}
          gap={3}
          flexDirection="column"
          alignItems="flex-start"
          alignSelf="stretch"
        >
          <Typography variant="h6">{t('cart.summary')}</Typography>

          {/* Contenedor items */}
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            width="100%"
          >
            <Box
              display="flex"
              justifyContent="space-between"
              align-items="center"
            >
              <Typography variant="body2" color="text.secondary">{t('cart.hotels')}</Typography>
              <Typography variant="subtitle2">{Currency.USD} {totalHoteles.toFixed(2)}</Typography>
            </Box>

            <Divider />
            <Box
              display="flex"
              justifyContent="space-between"
              align-items="center"
            >
              <Typography variant="body2" color="text.secondary" textTransform="uppercase">{t('cart.total')}</Typography>
              <Typography variant="subtitle1" gutterBottom={false} fontWeight={600}>{Currency.USD} {totalHoteles.toFixed(2)}</Typography>
            </Box>
            <Divider />
          </Box>

          { state?.status === SALE_ORDER_STATUS.Sold ? 
            (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexDirection="column"
                height="100%"
                width="100%"
                gap={4}
              >
                <CheckRounded color="success" sx={{ fontSize: theme.typography.h1.fontSize }} />
                <Typography variant='h4'>
                  { t('cart.sold.success') }
                </Typography>
                <Box>
                  <Typography variant='subtitle1' textAlign="center">
                    { t('cart.sold.successMessage') }
                  </Typography>
                  <Typography variant='subtitle2' textAlign="center" fontWeight={600}>
                    {state?.id}
                  </Typography>
                </Box>
              </Box>
            ) :
            (
              <>
                <LoadingButton
                  loading={isSaving}
                  disabled={!saveEnabled}
                  onClick={handleConvertToSaleOrder}
                  variant="contained"
                  color="primary"
                  fullWidth
                >
                  {t('cart.checkout')}
                </LoadingButton>
                <Button
                  startIcon={<ShoppingCart />}
                  endIcon={<ExpandMore />}
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onClick={handleOpenMenu}
                >
                  {t('cart.keepBuying')}
                </Button>
                <Menu
                  anchorEl={anchorMenu}
                  open={open}
                  onClose={handleCloseMenu}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <MenuItem disabled >
                    <ListItemIcon>
                      <Flight fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('cart.flights')}</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handleCloseMenu}>
                    <ListItemIcon>
                      <Hotel fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('cart.hotels')}</ListItemText>
                  </MenuItem>
                  <MenuItem disabled >
                    <ListItemIcon>
                      <DirectionsCar fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('cart.cars')}</ListItemText>
                  </MenuItem>
                  <MenuItem disabled >
                    <ListItemIcon>
                      <DirectionsCar fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('cart.services')}</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            )
          }
        </Box>
      </Box>
    </Card>
  );
};
